// El porqué de las condiciones del crédito: con qué plan se cotizó, qué promociones y qué políticas
// pisaron los términos. Lógica PURA — sin React y sin fetch — que alimenta la card del Resumen.
import { FoldableTermEnum } from '@fiado/type-kit/bin/loanOfferings/enums/FoldableTermEnum';
import { PromotionEffectKindEnum } from '@fiado/type-kit/bin/loanOfferings/enums/PromotionEffectKindEnum';
import type { AppliedPromotionSnapshot } from '@fiado/type-kit/bin/loanCredit/dtos/AppliedPromotionSnapshot';
import type { AppliedStorePolicySnapshot } from '@fiado/type-kit/bin/loanCredit/dtos/AppliedStorePolicySnapshot';
import type { CandidatePlanSnapshot } from '@fiado/type-kit/bin/loanCredit/dtos/CandidatePlanSnapshot';

/**
 * En qué estado llegó un bloque del porqué. `missing` es que el silo NO lo emite —el campo vino
 * ausente— y `empty` que lo emitió y ninguno aplicó. Decirlos con el mismo texto convierte un
 * hueco de dato en una afirmación que nadie sostuvo.
 */
export type RationalePresence = 'missing' | 'empty' | 'present';

/** Los renglones de «Condiciones del crédito» que una promoción o una política puede mover. */
export type OriginationTerm = 'downPayment' | 'tnaAnnual' | 'financedCapital';

/**
 * Qué renglón mueve cada palanca de promoción. `PCF_BONUS` no mueve ninguno —es un bono que se
 * abona a la tarjeta, no un término del crédito— y `COMBO` pisó dos o más sin decir cuáles.
 */
const TERM_BY_PROMOTION_EFFECT: Partial<Record<PromotionEffectKindEnum, OriginationTerm>> = {
    [PromotionEffectKindEnum.DOWN_PAYMENT_DELTA]: 'downPayment',
    [PromotionEffectKindEnum.TNA_OVERRIDE]: 'tnaAnnual',
    [PromotionEffectKindEnum.MAX_AMOUNT_OVERRIDE]: 'financedCapital',
};

/** El término que declara una política es la clave literal del contrato: no hay que interpretarla. */
const TERM_BY_FOLDABLE: Record<FoldableTermEnum, OriginationTerm> = {
    [FoldableTermEnum.MIN_DOWN_PAYMENT_PCT]: 'downPayment',
    [FoldableTermEnum.TNA_ANNUAL]: 'tnaAnnual',
    [FoldableTermEnum.MAX_FINANCED_AMOUNT_CENTS]: 'financedCapital',
};

/** Cómo se nombra cada palanca en ESTA card: con el renglón de condiciones que movió. */
const PROMOTION_EFFECT_LABEL: Record<PromotionEffectKindEnum, string> = {
    [PromotionEffectKindEnum.DOWN_PAYMENT_DELTA]: 'Enganche',
    [PromotionEffectKindEnum.PCF_BONUS]: 'Bono PCF',
    [PromotionEffectKindEnum.TNA_OVERRIDE]: 'Tasa anual',
    [PromotionEffectKindEnum.MAX_AMOUNT_OVERRIDE]: 'Monto máximo',
    [PromotionEffectKindEnum.COMBO]: 'Varios términos',
};

const FOLDABLE_TERM_LABEL: Record<FoldableTermEnum, string> = {
    [FoldableTermEnum.MIN_DOWN_PAYMENT_PCT]: 'Enganche mínimo',
    [FoldableTermEnum.TNA_ANNUAL]: 'Tasa anual',
    [FoldableTermEnum.MAX_FINANCED_AMOUNT_CENTS]: 'Monto máximo financiable',
};

/** El catálogo de palancas lo gobierna loan-offerings y puede crecer: una nueva se pinta cruda. */
export function promotionEffectLabel(kind: PromotionEffectKindEnum): string {
    return (PROMOTION_EFFECT_LABEL as Record<string, string | undefined>)[kind] ?? kind;
}

/** Igual que las palancas: un término que el catálogo agregue después se pinta con su clave. */
export function foldableTermLabel(term: FoldableTermEnum): string {
    return (FOLDABLE_TERM_LABEL as Record<string, string | undefined>)[term] ?? term;
}

function byNameEsMx(a: { name: string }, b: { name: string }): number {
    return a.name.localeCompare(b.name, 'es-MX');
}

/** El crédito visto por esta card: solo los campos congelados al cotizar, todos opcionales. */
export interface CreditRationaleInput {
    planId: string;
    appliedPromotion?: AppliedPromotionSnapshot | null;
    appliedPromotions?: readonly AppliedPromotionSnapshot[] | null;
    appliedStorePolicies?: readonly AppliedStorePolicySnapshot[] | null;
    candidatePlans?: readonly CandidatePlanSnapshot[] | null;
}

/** Un bloque del porqué: en qué estado llegó y qué se puede nombrar de él. */
export interface RationaleBlock<T> {
    presence: RationalePresence;
    items: readonly T[];
}

/**
 * Las promociones que explican esta cuota. El arreglo plural es la auditoría completa; la primaria
 * se le suma cuando el crédito la guardó y el arreglo no la trae —los originados antes del plural
 * solo tienen esa—. Con los dos campos ausentes el bloque es `missing`, no vacío.
 */
export function promotionsBlockOf(
    credit: CreditRationaleInput,
): RationaleBlock<AppliedPromotionSnapshot> {
    const primary = credit.appliedPromotion;
    const all = credit.appliedPromotions;
    if (all == null && primary == null) return { presence: 'missing', items: [] };

    const items = [...(all ?? [])];
    if (primary && !items.some((item) => item.promotionId === primary.promotionId)) {
        items.push(primary);
    }
    items.sort(byNameEsMx);
    return { presence: items.length === 0 ? 'empty' : 'present', items };
}

export function storePoliciesBlockOf(
    credit: CreditRationaleInput,
): RationaleBlock<AppliedStorePolicySnapshot> {
    const policies = credit.appliedStorePolicies;
    if (policies == null) return { presence: 'missing', items: [] };
    const items = [...policies].sort(byNameEsMx);
    return { presence: items.length === 0 ? 'empty' : 'present', items };
}

/** El plan con el que se cotizó y los que compitieron por el mismo crédito. */
export interface PlanRationale {
    presence: RationalePresence;
    /** Código del plan cotizado, resuelto contra los candidatos; sin ellos, el id del crédito. */
    code: string;
    /** Los otros planes que compitieron, por código y en orden alfabético. */
    rivals: readonly string[];
}

export function planRationaleOf(credit: CreditRationaleInput): PlanRationale {
    const candidates = credit.candidatePlans;
    const chosen = candidates?.find((plan) => plan.planId === credit.planId);
    const rivals = (candidates ?? [])
        .filter((plan) => plan.planId !== credit.planId)
        .map((plan) => plan.code)
        .sort((a, b) => a.localeCompare(b, 'es-MX'));
    return {
        presence: candidates == null ? 'missing' : candidates.length === 0 ? 'empty' : 'present',
        code: chosen?.code ?? credit.planId,
        rivals,
    };
}

/** Quién movió un renglón de «Condiciones del crédito», por nombre y en orden alfabético. */
export interface TermOverride {
    promotions: readonly string[];
    policies: readonly string[];
}

export type TermOverrides = Readonly<Record<OriginationTerm, TermOverride>>;

/**
 * Qué renglón de las condiciones dejó de ser el del plan pelado y por culpa de quién. Sale de lo
 * que cada bloque declara haber ganado: las palancas de las promociones y los términos literales
 * de las políticas. Lo que no nombra un término no marca ningún renglón.
 */
export function termOverridesOf(credit: CreditRationaleInput): TermOverrides {
    const promotions = new Map<OriginationTerm, string[]>();
    const policies = new Map<OriginationTerm, string[]>();

    const push = (map: Map<OriginationTerm, string[]>, term: OriginationTerm, name: string) => {
        const names = map.get(term) ?? [];
        if (!names.includes(name)) names.push(name);
        map.set(term, names);
    };

    for (const promotion of promotionsBlockOf(credit).items) {
        for (const effect of promotion.appliedEffects) {
            const term = TERM_BY_PROMOTION_EFFECT[effect];
            if (term) push(promotions, term, promotion.name);
        }
    }
    for (const policy of storePoliciesBlockOf(credit).items) {
        for (const applied of policy.appliedTerms) {
            const term = (TERM_BY_FOLDABLE as Record<string, OriginationTerm | undefined>)[applied];
            if (term) push(policies, term, policy.name);
        }
    }

    const sorted = (names: string[] | undefined): readonly string[] =>
        [...(names ?? [])].sort((a, b) => a.localeCompare(b, 'es-MX'));

    return {
        downPayment: {
            promotions: sorted(promotions.get('downPayment')),
            policies: sorted(policies.get('downPayment')),
        },
        financedCapital: {
            promotions: sorted(promotions.get('financedCapital')),
            policies: sorted(policies.get('financedCapital')),
        },
        tnaAnnual: {
            promotions: sorted(promotions.get('tnaAnnual')),
            policies: sorted(policies.get('tnaAnnual')),
        },
    };
}

function quotedList(names: readonly string[]): string {
    return names.map((name) => `«${name}»`).join(', ');
}

/**
 * La frase que explica una marca: quién movió ese valor. `undefined` cuando nadie lo movió, para
 * que el renglón quede sin marca en vez de con una marca que no dice nada.
 */
export function overrideNoteOf(override: TermOverride): string | undefined {
    const parts: string[] = [];
    if (override.promotions.length > 0) {
        parts.push(
            `la ${override.promotions.length === 1 ? 'promoción' : 'promociones'} ${quotedList(override.promotions)}`,
        );
    }
    if (override.policies.length > 0) {
        parts.push(
            `la ${override.policies.length === 1 ? 'política' : 'políticas'} ${quotedList(override.policies)}`,
        );
    }
    if (parts.length === 0) return undefined;
    return `Este valor no es el del plan: lo movió ${parts.join(' y ')}.`;
}

const SHARE_PCT = new Intl.NumberFormat('es-MX', {
    style: 'percent',
    maximumFractionDigits: 2,
});

/** Un decimal sobre 1 (`0.10`) a porcentaje. Es como viajan las cotas de enganche. */
export function formatSharePct(share: number): string {
    return SHARE_PCT.format(share);
}

/**
 * El tope de enganche. `null` es un HECHO —el plan no acota cuánto puede enganchar el cliente—, no
 * un dato que falte: por eso se dice con palabras y no con el centinela de «sin fuente».
 */
export const NO_DOWN_PAYMENT_CAP_TEXT = 'sin tope';
